import type { ApiNode } from '@goalbuddy/client';
import { canHaveParent, NODE_TYPES, type NodeType } from '@goalbuddy/core';
import { Graph, layout } from '@dagrejs/dagre';
import { useMutation } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Check, ChevronRight, Lightbulb, MoreHorizontal, Plus, Zap } from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { toast } from 'sonner';
import { TypeIcon } from '@/components/node-meta';
import { Button, Input, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { areaColor } from '@/lib/area';
import { useDetail, useNodes, useUsers } from '@/lib/hooks';
import { cn } from '@/lib/utils';

const W = 220;
const H = 50;
const ADDABLE: readonly NodeType[] = NODE_TYPES;

type Info = {
  color: string | null;
  childCount: number;
  progress: { done: number; total: number } | null;
  overdue: boolean;
};
type MapData = { node: ApiNode; info: Info; dropTarget?: 'ok' | 'bad' };
type MapNode = Node<MapData, 'gb'>;

interface MapCtx {
  expanded: Set<number>;
  selectedId: number | null;
  toggle: (id: number) => void;
  openAdd: (node: ApiNode, anchor: DOMRect) => void;
  openMenu: (node: ApiNode, anchor: DOMRect) => void;
}
const Ctx = createContext<MapCtx | null>(null);

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());

function GbNode({ data }: NodeProps<MapNode>) {
  const ctx = useContext(Ctx)!;
  const n = data.node;
  const { info } = data;
  const selected = ctx.selectedId === n.id;
  const isDone = n.status === 'done';
  const expanded = ctx.expanded.has(n.id);
  const pct = info.progress ? info.progress.done / info.progress.total : 0;
  return (
    <div
      data-map-node={n.id}
      className={cn(
        'group relative flex items-center gap-2 rounded-xl border bg-surface py-1.5 pr-2 pl-3 text-xs shadow-[var(--shadow-card)] transition-shadow',
        selected ? 'border-primary shadow-md ring-2 ring-primary/30' : 'border-border',
        n.type === 'idea' && 'border-dashed border-amber-400',
        isDone && 'opacity-60',
        data.dropTarget === 'ok' && 'ring-2 ring-ok',
        data.dropTarget === 'bad' && 'ring-2 ring-danger',
      )}
      style={{ width: W, minHeight: H, borderLeft: `4px solid ${info.color ?? 'var(--color-border)'}` }}
    >
      <Handle type="target" position={Position.Left} className="!bg-border" />
      {isDone ? <Check size={14} className="shrink-0 text-ok" strokeWidth={3} /> : <TypeIcon type={n.type} />}
      <span className={cn('line-clamp-2 min-w-0 flex-1 font-medium leading-snug', isDone && 'line-through')}>
        {n.title}
      </span>
      {info.overdue && !isDone && (
        <span className="h-2 w-2 shrink-0 rounded-full bg-danger" title="Overdue" />
      )}
      {n.is_next_action && !isDone && <Zap size={12} className="shrink-0 text-primary" />}
      {info.progress && (
        <span
          className="shrink-0 rounded-md bg-muted px-1 py-0.5 text-[10px] font-semibold tabular-nums text-muted-fg"
          title={`${info.progress.done} of ${info.progress.total} done`}
        >
          {Math.round(pct * 100)}%
        </span>
      )}
      {info.childCount > 0 && (
        <button
          className="nodrag nopan flex shrink-0 items-center gap-0.5 rounded-md bg-muted px-1 py-0.5 text-[10px] font-semibold text-muted-fg hover:bg-accent hover:text-primary"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            ctx.toggle(n.id);
          }}
        >
          <ChevronRight size={11} className={cn('transition-transform', expanded && 'rotate-90')} />
          {info.childCount}
        </button>
      )}
      <div
        className={cn(
          'nodrag nopan absolute -top-3 right-2 hidden gap-1 group-hover:flex',
          selected && 'flex',
        )}
      >
        <button
          className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted-fg shadow-sm hover:text-primary"
          aria-label="Add child"
          title="Add child (Tab)"
          onClick={(e) => {
            e.stopPropagation();
            ctx.openAdd(n, e.currentTarget.getBoundingClientRect());
          }}
        >
          <Plus size={13} />
        </button>
        <button
          className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted-fg shadow-sm hover:text-fg"
          aria-label="More actions"
          onClick={(e) => {
            e.stopPropagation();
            ctx.openMenu(n, e.currentTarget.getBoundingClientRect());
          }}
        >
          <MoreHorizontal size={13} />
        </button>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-border" />
    </div>
  );
}

const nodeTypes = { gb: GbNode };

function layoutTree(nodes: ApiNode[], infos: Map<number, Info>): { nodes: MapNode[]; edges: Edge[] } {
  const g = new Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 14, ranksep: 70 });
  g.setDefaultEdgeLabel(() => ({}));
  const ids = new Set(nodes.map((n) => n.id));
  for (const n of nodes) g.setNode(String(n.id), { width: W, height: H });
  const edges: Edge[] = [];
  for (const n of nodes) {
    if (n.parent_id !== null && ids.has(n.parent_id)) {
      g.setEdge(String(n.parent_id), String(n.id));
      edges.push({
        id: `${n.parent_id}-${n.id}`,
        source: String(n.parent_id),
        target: String(n.id),
        type: 'smoothstep',
      });
    }
  }
  layout(g);
  return {
    nodes: nodes.map((n) => {
      const p = g.node(String(n.id));
      return {
        id: String(n.id),
        type: 'gb',
        position: { x: p.x - W / 2, y: p.y - H / 2 },
        data: { node: n, info: infos.get(n.id)! },
      };
    }),
    edges,
  };
}

type Popover =
  | { kind: 'add'; parent: ApiNode | null; sibling?: boolean; x: number; y: number }
  | { kind: 'rename'; node: ApiNode; x: number; y: number }
  | { kind: 'menu'; node: ApiNode; x: number; y: number };

function clamp(x: number, y: number, w: number, h: number) {
  return {
    left: Math.max(8, Math.min(x, window.innerWidth - w - 8)),
    top: Math.max(8, Math.min(y, window.innerHeight - h - 8)),
  };
}

function AddPopover({
  parent,
  x,
  y,
  onClose,
  onCreated,
}: {
  parent: ApiNode | null;
  x: number;
  y: number;
  onClose: () => void;
  onCreated: (parentId: number | null) => void;
}) {
  const allowed = ADDABLE.filter((t) => canHaveParent(t, parent ? parent.type : null));
  const [type, setType] = useState<NodeType>(allowed.includes('task') ? 'task' : (allowed[0] ?? 'idea'));
  const [title, setTitle] = useState('');
  const create = useMutation({
    mutationFn: () => api.createNode({ type, title: title.trim(), parent_id: parent?.id ?? null }),
    onSuccess: () => {
      setTitle('');
      onCreated(parent?.id ?? null);
    },
  });
  const pos = clamp(x, y, 300, 110);
  return (
    <div
      className="fixed z-50 w-[300px] animate-fade-in rounded-xl border border-border bg-surface p-3 shadow-[var(--shadow-pop)]"
      style={pos}
    >
      <div className="mb-2 truncate text-xs font-medium text-muted-fg">
        Add to {parent ? parent.title : 'top level'}
      </div>
      {allowed.length === 0 ? (
        <div className="text-sm text-muted-fg">Nothing can be added under a {parent?.type}.</div>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) create.mutate();
          }}
        >
          <div className="flex flex-wrap gap-1">
            {allowed.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={cn(
                  'flex items-center gap-1 rounded-lg border px-2 py-1 text-xs',
                  type === t ? 'border-primary bg-accent text-primary' : 'border-border hover:bg-muted',
                )}
              >
                <TypeIcon type={t} size={12} /> {t}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              autoFocus
              value={title}
              placeholder={`New ${type}…`}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && onClose()}
            />
            <Button type="submit" disabled={!title.trim() || create.isPending}>
              Add
            </Button>
          </div>
          <div className="text-[11px] text-muted-fg">Enter adds and keeps this open · Esc closes</div>
        </form>
      )}
    </div>
  );
}

function RenamePopover({
  node,
  x,
  y,
  onClose,
}: {
  node: ApiNode;
  x: number;
  y: number;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(node.title);
  const save = useMutation({
    mutationFn: () => api.updateNode(node.id, { title: title.trim() }),
    onSuccess: onClose,
  });
  return (
    <form
      className="fixed z-50 flex w-[300px] animate-fade-in gap-2 rounded-xl border border-border bg-surface p-3 shadow-[var(--shadow-pop)]"
      style={clamp(x, y, 300, 60)}
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim() && title.trim() !== node.title) save.mutate();
        else onClose();
      }}
    >
      <Input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />
      <Button type="submit">Save</Button>
    </form>
  );
}

function MenuItem({
  children,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      className={cn(
        'block w-full rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-muted',
        danger && 'text-danger',
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function MapInner({ wrapRef }: { wrapRef: RefObject<HTMLDivElement | null> }) {
  const { data: all } = useNodes({ include_done: true, limit: 1000 });
  const { data: users } = useUsers();
  const [root, setRoot] = useState<string>('');
  const [hideDone, setHideDone] = useState(false);
  const [expanded, setExpanded] = useState<Set<number> | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pop, setPop] = useState<Popover | null>(null);
  const { open } = useDetail();
  const [ideaTitle, setIdeaTitle] = useState('');
  const { getIntersectingNodes, fitView } = useReactFlow();

  const scoped = useMemo(() => {
    const nodes = (all ?? []).filter((n) => !hideDone || (n.status !== 'done' && n.status !== 'dropped'));
    if (!root) return nodes;
    const keep = new Set<number>([Number(root)]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (n.parent_id !== null && keep.has(n.parent_id) && !keep.has(n.id)) {
          keep.add(n.id);
          grew = true;
        }
      }
    }
    return nodes.filter((n) => keep.has(n.id));
  }, [all, root, hideDone]);

  const { childrenOf, depthOf, infos } = useMemo(() => {
    const childrenOf = new Map<number, ApiNode[]>();
    const ids = new Set(scoped.map((n) => n.id));
    for (const n of scoped) {
      if (n.parent_id !== null && ids.has(n.parent_id))
        childrenOf.set(n.parent_id, [...(childrenOf.get(n.parent_id) ?? []), n]);
    }
    const depthOf = new Map<number, number>();
    const walk = (n: ApiNode, d: number) => {
      depthOf.set(n.id, d);
      (childrenOf.get(n.id) ?? []).forEach((c) => walk(c, d + 1));
    };
    scoped.filter((n) => n.parent_id === null || !ids.has(n.parent_id)).forEach((n) => walk(n, 0));
    const descendants = (id: number): ApiNode[] =>
      (childrenOf.get(id) ?? []).flatMap((c) => [c, ...descendants(c.id)]);
    const day = today();
    const infos = new Map<number, Info>();
    for (const n of scoped) {
      const desc = descendants(n.id);
      const tasks = desc.filter((d) => d.type === 'task');
      const pool = tasks.length
        ? tasks
        : desc.filter((d) => d.type === 'result' || d.type === 'goal' || d.type === 'project');
      infos.set(n.id, {
        color: areaColor(all, n.id),
        childCount: childrenOf.get(n.id)?.length ?? 0,
        progress:
          pool.length && n.type !== 'task'
            ? { done: pool.filter((d) => d.status === 'done').length, total: pool.length }
            : null,
        overdue: !!n.due_date && n.due_date < day,
      });
    }
    return { childrenOf, depthOf, infos };
  }, [scoped, all]);

  // Default: top level and first children visible (areas expanded, goals collapsed).
  const expandedSet = useMemo(
    () => expanded ?? new Set(scoped.filter((n) => (depthOf.get(n.id) ?? 0) < 1).map((n) => n.id)),
    [expanded, scoped, depthOf],
  );

  const visible = useMemo(() => {
    const byId = new Map(scoped.map((n) => [n.id, n]));
    return scoped.filter((n) => {
      let cur = n;
      for (let i = 0; i < 64 && cur.parent_id !== null; i++) {
        const p = byId.get(cur.parent_id);
        if (!p) return true;
        if (!expandedSet.has(p.id)) return false;
        cur = p;
      }
      return true;
    });
  }, [scoped, expandedSet]);

  const laid = useMemo(() => layoutTree(visible, infos), [visible, infos]);
  const [nodes, setNodes, onNodesChange] = useNodesState<MapNode>(laid.nodes);
  useEffect(() => setNodes(laid.nodes), [laid, setNodes]);

  const setDepth = (d: number) => {
    setExpanded(new Set(scoped.filter((n) => (depthOf.get(n.id) ?? 0) < d).map((n) => n.id)));
    setTimeout(() => fitView({ duration: 300, padding: 0.15 }), 50);
  };
  const toggle = (id: number) => {
    const next = new Set(expandedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };
  const reveal = (id: number | null) => {
    if (id === null) return;
    const next = new Set(expandedSet);
    next.add(id);
    setExpanded(next);
  };

  const move = useMutation({
    mutationFn: (v: { id: number; parent: number }) => api.moveNode(v.id, v.parent),
  });
  const convert = useMutation({
    mutationFn: (v: { id: number; type: 'task' | 'goal' | 'result' }) => api.convertNode(v.id, v.type),
  });
  const done = useMutation({ mutationFn: (id: number) => api.doneNode(id) });
  const update = useMutation({
    mutationFn: (v: { id: number } & Parameters<typeof api.updateNode>[1]) => api.updateNode(v.id, v),
  });
  const archive = useMutation({ mutationFn: (id: number) => api.archiveNode(id) });
  const addIdea = useMutation({
    mutationFn: () =>
      api.createNode({ type: 'idea', title: ideaTitle.trim(), parent_id: root ? Number(root) : null }),
    onSuccess: () => setIdeaTitle(''),
  });

  const nodeEl = (id: number) =>
    wrapRef.current?.querySelector(`[data-map-node="${id}"]`)?.getBoundingClientRect();
  const openAdd = (n: ApiNode, a?: DOMRect) => {
    const r = a ?? nodeEl(n.id);
    setPop({ kind: 'add', parent: n, x: (r?.right ?? 200) + 8, y: r?.top ?? 200 });
    reveal(n.id);
  };

  const ctx: MapCtx = {
    expanded: expandedSet,
    selectedId,
    toggle,
    openAdd: (n, a) => openAdd(n, a),
    openMenu: (n, a) => setPop({ kind: 'menu', node: n, x: a.left, y: a.bottom + 4 }),
  };

  // Keyboard: Tab adds a child, Enter adds a sibling, F2 renames, Space expands or collapses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pop) {
        setPop(null);
        return;
      }
      const t = e.target as HTMLElement;
      if (
        pop ||
        t.closest('input,textarea,select,[role="dialog"]') ||
        selectedId === null ||
        e.metaKey ||
        e.ctrlKey
      )
        return;
      const n = (all ?? []).find((x) => x.id === selectedId);
      if (!n) return;
      if (e.key === 'Tab') {
        e.preventDefault();
        openAdd(n);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const parent = (all ?? []).find((x) => x.id === n.parent_id) ?? null;
        const r = nodeEl(n.id);
        setPop({ kind: 'add', parent, sibling: true, x: (r?.right ?? 200) + 8, y: r?.top ?? 200 });
      } else if (e.key === 'F2') {
        e.preventDefault();
        const r = nodeEl(n.id);
        setPop({ kind: 'rename', node: n, x: r?.left ?? 200, y: (r?.bottom ?? 200) + 4 });
      } else if (e.key === ' ' && (childrenOf.get(n.id)?.length ?? 0) > 0) {
        e.preventDefault();
        toggle(n.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const targetFor = (dragged: MapNode) => {
    const hit = getIntersectingNodes(dragged).find((n) => n.id !== dragged.id) as MapNode | undefined;
    if (!hit) return null;
    const ok =
      canHaveParent(dragged.data.node.type, hit.data.node.type as NodeType) &&
      hit.data.node.id !== dragged.data.node.parent_id;
    return { hit, ok };
  };

  const areas = (all ?? []).filter(
    (n) => n.type === 'area' || (n.parent_id === null && n.type !== 'task' && n.type !== 'idea'),
  );
  const rootNode = root ? (all ?? []).find((n) => n.id === Number(root)) : null;
  const menuNode = pop?.kind === 'menu' ? pop.node : null;
  const closePop = () => setPop(null);

  return (
    <Ctx.Provider value={ctx}>
      <div className="flex h-full flex-col">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-surface/60 px-4 py-3 md:px-6">
          <div className="mr-auto min-w-0">
            <h1 className="text-lg font-semibold leading-tight">Map</h1>
            <p className="hidden text-xs text-muted-fg lg:block">
              Select a node, then Tab adds a child, Enter a sibling, F2 renames. Drag onto another node to
              move.
            </p>
          </div>
          <div
            className="flex items-center rounded-lg border border-border bg-surface p-0.5 text-xs font-medium"
            role="group"
            aria-label="Depth"
          >
            {[
              ['Areas', 1],
              ['Goals', 2],
              ['Results', 3],
              ['All', 99],
            ].map(([label, d]) => (
              <button
                key={label}
                className="rounded-md px-2.5 py-1.5 text-muted-fg hover:bg-muted hover:text-fg"
                onClick={() => setDepth(d as number)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-fg">
            <input
              type="checkbox"
              className="accent-[var(--color-primary)]"
              checked={hideDone}
              onChange={(e) => setHideDone(e.target.checked)}
            />{' '}
            Hide done
          </label>
          <Select
            value={root}
            onChange={(e) => {
              setRoot(e.target.value);
              setExpanded(null);
              setTimeout(() => fitView({ duration: 300, padding: 0.15 }), 80);
            }}
            className="w-44"
            aria-label="Focus on"
          >
            <option value="">Everything</option>
            {rootNode && !areas.some((a) => a.id === rootNode.id) && (
              <option value={rootNode.id}>{rootNode.title}</option>
            )}
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </Select>
          <form
            className="flex w-full gap-2 sm:w-auto"
            onSubmit={(e) => {
              e.preventDefault();
              if (ideaTitle.trim()) addIdea.mutate();
            }}
          >
            <Input
              value={ideaTitle}
              onChange={(e) => setIdeaTitle(e.target.value)}
              className="sm:w-52"
              placeholder={root ? 'Quick idea under focus…' : 'Quick idea (inbox)…'}
            />
            <Button type="submit" variant="outline" disabled={!ideaTitle.trim()}>
              <Lightbulb size={14} /> Idea
            </Button>
          </form>
        </div>
        <div
          ref={wrapRef}
          tabIndex={-1}
          className="relative min-h-0 flex-1 overflow-hidden bg-surface outline-none"
        >
          <ReactFlow
            nodes={nodes}
            edges={laid.edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            fitView
            colorMode="system"
            minZoom={0.1}
            nodesConnectable={false}
            deleteKeyCode={null}
            onNodeClick={(_, n) => {
              setSelectedId(Number(n.id));
              wrapRef.current?.focus();
            }}
            onNodeDoubleClick={(_, n) => open(Number(n.id))}
            onNodeDrag={(_, dragged) => {
              const t = targetFor(dragged as MapNode);
              setNodes((ns) =>
                ns.map((n) => ({
                  ...n,
                  data: { ...n.data, dropTarget: t && n.id === t.hit.id ? (t.ok ? 'ok' : 'bad') : undefined },
                })),
              );
            }}
            onNodeDragStop={(_, dragged) => {
              const t = targetFor(dragged as MapNode);
              setNodes(laid.nodes);
              if (!t) return;
              if (!t.ok) {
                toast.error(
                  `A ${(dragged as MapNode).data.node.type} cannot go under a ${t.hit.data.node.type}`,
                );
                return;
              }
              reveal(Number(t.hit.id));
              move.mutate({ id: Number(dragged.id), parent: Number(t.hit.id) });
            }}
            onNodeContextMenu={(e, n) => {
              e.preventDefault();
              setSelectedId(Number(n.id));
              setPop({ kind: 'menu', node: (n as MapNode).data.node, x: e.clientX, y: e.clientY });
            }}
            onPaneClick={() => {
              setPop(null);
              setSelectedId(null);
            }}
          >
            <Background />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </div>
        {pop && (
          <div
            className="fixed inset-0 z-40"
            onClick={closePop}
            onContextMenu={(e) => (e.preventDefault(), closePop())}
          />
        )}
        {pop?.kind === 'add' && (
          <AddPopover
            key={`${pop.parent?.id ?? 'top'}`}
            parent={pop.parent}
            x={pop.x}
            y={pop.y}
            onClose={closePop}
            onCreated={(pid) => reveal(pid)}
          />
        )}
        {pop?.kind === 'rename' && <RenamePopover node={pop.node} x={pop.x} y={pop.y} onClose={closePop} />}
        {menuNode && (
          <div
            className="fixed z-50 min-w-52 animate-fade-in rounded-xl border border-border bg-surface p-1 shadow-[var(--shadow-pop)]"
            style={clamp(pop!.x, pop!.y, 220, 380)}
          >
            <MenuItem
              onClick={() => {
                open(menuNode.id);
                closePop();
              }}
            >
              Open details
            </MenuItem>
            <MenuItem onClick={() => openAdd(menuNode)}>Add child…</MenuItem>
            <MenuItem onClick={() => setPop({ kind: 'rename', node: menuNode, x: pop!.x, y: pop!.y })}>
              Rename
            </MenuItem>
            {(childrenOf.get(menuNode.id)?.length ?? 0) > 0 && (
              <MenuItem
                onClick={() => {
                  toggle(menuNode.id);
                  closePop();
                }}
              >
                {expandedSet.has(menuNode.id) ? 'Collapse' : 'Expand'}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setRoot(String(menuNode.id));
                setExpanded(null);
                setTimeout(() => fitView({ duration: 300, padding: 0.15 }), 80);
                closePop();
              }}
            >
              Focus on this branch
            </MenuItem>
            <div className="my-1 h-px bg-border" />
            {['task', 'result', 'goal', 'project', 'idea'].includes(menuNode.type) && (
              <MenuItem
                onClick={() => {
                  if (menuNode.status === 'done') update.mutate({ id: menuNode.id, status: 'active' });
                  else done.mutate(menuNode.id);
                  closePop();
                }}
              >
                {menuNode.status === 'done' ? 'Reopen' : 'Mark done'}
              </MenuItem>
            )}
            {menuNode.type === 'task' && (
              <MenuItem
                onClick={() => {
                  update.mutate({ id: menuNode.id, is_next_action: !menuNode.is_next_action });
                  closePop();
                }}
              >
                {menuNode.is_next_action ? 'Unmark next action' : 'Mark as next action'}
              </MenuItem>
            )}
            {users
              ?.filter((u) => u.kind === 'agent' && u.role !== 'system')
              .map((u) => (
                <MenuItem
                  key={u.id}
                  onClick={() => {
                    update.mutate({ id: menuNode.id, assignee_id: u.id });
                    closePop();
                  }}
                >
                  Assign to {u.name}
                </MenuItem>
              ))}
            {menuNode.type === 'idea' &&
              (['task', 'goal', 'result'] as const).map((t) => (
                <MenuItem
                  key={t}
                  onClick={() => {
                    convert.mutate({ id: menuNode.id, type: t });
                    closePop();
                  }}
                >
                  Convert to {t}
                </MenuItem>
              ))}
            <div className="my-1 h-px bg-border" />
            <MenuItem
              danger
              onClick={() => {
                if (confirm(`Archive "${menuNode.title}" and everything under it?`))
                  archive.mutate(menuNode.id);
                closePop();
              }}
            >
              Archive
            </MenuItem>
          </div>
        )}
      </div>
    </Ctx.Provider>
  );
}

export function MapPage() {
  const wrapRef = useRef<HTMLDivElement>(null);
  return (
    <ReactFlowProvider>
      <MapInner wrapRef={wrapRef} />
    </ReactFlowProvider>
  );
}
